import { useMemo } from "react";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { TMX_EVENT_TYPE_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { TMX_EVENT_TYPE_OPTIONS } from "@constants/tmx-event-type-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { PropertiesSectionDescription } from "../properties-section-header/properties-section-description";
import styles from "./tmx-event-type-container.less";

type TmxEventTypeContainerProps = {
    isDisabled: boolean;
    pageLevel: PageLevelTypes;
    workflowId: string;
    tmxEventType: string;
    inheritedTmxEventType: string;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean; }>) => void;
}

export const TmxEventTypeContainer = (props: TmxEventTypeContainerProps) => {
    const {
        pageLevel,
        workflowId,
        tmxEventType,
        inheritedTmxEventType,
        updateProperties
    } = props;


    const isServiceWithoutWorkflow = useMemo(() => {
        return pageLevel === PageLevelTypes.APIKEY && !workflowId;
    }, [pageLevel, workflowId]);

    const isResetSetTmxEventTypeDisabled = useMemo(() => {
        //disable reset if at workflow level OR its a service without a workflow OR there is not a workflow property to reset to
        return (
            props.isDisabled ||
            pageLevel === PageLevelTypes.WORKFLOW ||
            isServiceWithoutWorkflow ||
            !!inheritedTmxEventType === !!tmxEventType
        );
    }, [props.isDisabled, pageLevel, isServiceWithoutWorkflow, inheritedTmxEventType, tmxEventType]);

    const isResetTmxEventTypeDisabled = useMemo(() => {
        //disable reset for same conditions as above OR if the configured value from service and workflow match
        return (
            props.isDisabled ||
            pageLevel === PageLevelTypes.WORKFLOW ||
            isServiceWithoutWorkflow ||
            !inheritedTmxEventType ||
            (inheritedTmxEventType && tmxEventType && inheritedTmxEventType === tmxEventType)
        );
    }, [props.isDisabled, pageLevel, isServiceWithoutWorkflow, inheritedTmxEventType, tmxEventType]);

    const isInputDisabled = useMemo(() => {
        //disable inputs if there is a workflow
        return props.isDisabled || (pageLevel === PageLevelTypes.APIKEY && !!workflowId);
    }, [
        props.isDisabled,
        pageLevel,
        workflowId
    ]);

    const handleResetSetTMXEventType = () => {
        updateProperties([
            { propertyTypeId: TMX_EVENT_TYPE_PROPERTY_TYPE_ID, newValue: null, added: false }
        ]);
    };

    const onSetTmxEventTypeChange = (value: string) => {
        const enabled = isStringTrue(value);
        if (enabled) {
            updateProperties([{ propertyTypeId: TMX_EVENT_TYPE_PROPERTY_TYPE_ID, newValue: TMX_EVENT_TYPE_OPTIONS[0].value, added: enabled }]);
        } else {
            updateProperties([{ propertyTypeId: TMX_EVENT_TYPE_PROPERTY_TYPE_ID, newValue: null, added: false }]);
        }
    };

    const onTmxEventTypeChange = (option: SelectOption) => {
        updateProperties([{ propertyTypeId: TMX_EVENT_TYPE_PROPERTY_TYPE_ID, newValue: option.value, added: true }]);
    };

    const tmxEventTypeResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.tmxEventType;

    // const documentationLink = useMemo(() => {
    //     if (documentationLinks.length) {
    //         const documentationLink = documentationLinks.find(doc => doc.shortName === 'tmx-event-type-widget');
    //         if (documentationLink) {
    //             return documentationLink.url;
    //         }
    //     }

    //     return '';
    // }, [documentationLinks]);

    const selectedTmxEventType = useMemo(() => {
        if (tmxEventType) {
            return TMX_EVENT_TYPE_OPTIONS.find(option => option.value === tmxEventType);
        }

        return null;
    }, [tmxEventType]);

    return (
        <div>
            <ContentPageHeader title={tmxEventTypeResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <PropertiesSectionDescription
                    description={tmxEventTypeResources.description}
                // documentationLink={documentationLink}
                />
                <div className={styles.tmxEventTypeContainer}>
                    <ThemingFormGroupWrapper
                        currentThemingPropertyValue={!!tmxEventType}
                        inheritedThemingPropertyValue={!!inheritedTmxEventType}
                        pageLevel={pageLevel}
                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                    >
                        <RadioInputFormGroup
                            controlId={"set-tmx-event-type-radio"}
                            automationId={"set-tmx-event-type-radio"}
                            disabled={isInputDisabled}
                            isRequired={false}
                            labelWidth={LabelWidthTypes.LARGE}
                            layout={LayoutTypes.COLUMN}
                            label={tmxEventTypeResources.inputs.setTmxEventType.label}
                            onChange={onSetTmxEventTypeChange}
                            value={tmxEventType ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                            options={BOOLEAN_RADIO_INPUT_OPTIONS}
                            afterLabelTooltipText={tmxEventTypeResources.inputs.setTmxEventType.tooltipText}
                            afterLabelIcon={isResetSetTmxEventTypeDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetSetTMXEventType}
                        />
                    </ThemingFormGroupWrapper>
                    {tmxEventType && (
                        <ThemingFormGroupWrapper
                            currentThemingPropertyValue={tmxEventType}
                            inheritedThemingPropertyValue={inheritedTmxEventType}
                            pageLevel={pageLevel}
                            hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW || (!!tmxEventType && !inheritedTmxEventType)}
                        >
                            <SelectFormGroup
                                controlId={'tmx-event-type-input'}
                                automationId={'tmx-event-type-input'}
                                label={tmxEventTypeResources.inputs.tmxEventType.label}
                                ariaLabel={tmxEventTypeResources.inputs.tmxEventType.label}
                                onChange={onTmxEventTypeChange}
                                selectedOption={selectedTmxEventType}
                                options={TMX_EVENT_TYPE_OPTIONS}
                                disabled={isInputDisabled}
                                isClearable={false}
                                isSearchable={false}
                                isRequired={false}
                                layout={LayoutTypes.COLUMN}
                                labelWidth={LabelWidthTypes.LARGE}
                                afterLabelIcon={isResetTmxEventTypeDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetSetTMXEventType}
                            />
                        </ThemingFormGroupWrapper>
                    )}
                </div>

            </ContentPageBody>
        </div>)
}